// =============================================================
//  audioPool.js -- randomised pool playback
//
//  Manages a cached pool of audio files selected by shuffle,
//  random, or sequential modes.  Each trigger picks a file,
//  evaluates randomisable params, and delegates to handleAudioCue.
//
//  Polyphony is unlimited by default -- overlapping voices are
//  allowed and each gets its own waveform peak layer + cursor.
//
//  Waveform lifecycle:
//    prime  -> base contour hidden, preview layers for up to 5 files
//    trigger -> previews cleared, per-voice coloured layer + cursor
//    voice end -> layer + cursor removed automatically
//    last voice end -> preview layers restored, info text hidden
//    next trigger -> previews cleared again, cycle repeats
// =============================================================

import { sendOSC } from "../../system/oscillaOSCClient.js";
import { listDir, filterPool, missNote } from "../../system/mediaFolder.js";

import {
  evalMaybeRandom,
  makePatternGenerator,
  createAudioOverlay,
  formatAudioPoolOverlay,
  parseOverlayLevel,
  audioBufferCache,
  sharedAudioCtx,
  selectFromPool,
  fmtNum,
  fetchAudioBuffer,
  buildAudioInfoHeader,
  getPlaybackClientsFromEl,
  nextBidirectionalSpeed,
  valueNow,
} from "./audioShared.js";

import { handleAudioCue, consoleBox } from "./audioFile.js";
import {
  renderWaveform,
  getWaveform,
  startCursor,
  addCursor,
  removeCursor,
  addPeakLayer,
  removePeakLayer,
  removeAllPeakLayers,
  updateWaveformLayout
} from "../../system/waveform.js";

import { registerTask, completeTask } from "../../system/loadTasks.js";
import { shuffle } from "./patterns.js";
import { onReset } from "../../system/teardown.js";

// =============================================================
//  Pool cache
// =============================================================

const audioPools = window.audioPools || (window.audioPools = new Map());

/**
 * Clear all pool caches. Useful after bulk file changes.
 */
export function clearAllPoolCaches() {
  audioPools.clear();
}

// =============================================================
//  Speed pattern generators (for pitch pattern mode)
// =============================================================

const speedPatternGenerators = new Map();
onReset("project", function forgetSpeedPatterns() { speedPatternGenerators.clear(); });

/**
 * Create a pattern generator similar to synth.js.
 * Supports Prand, Pxrand, Pshuf, Pseq.
 */
function makeSpeedPatternGenerator(patternName, values) {
  let index = 0;
  let last = null;
  let shuffled = patternName === "Pshuf" ? shuffle(values) : null;

  return {
    next() {
      if (values.length === 0) return 1;

      if (patternName === "Prand") {
        // Random selection (repeats allowed)
        return values[Math.floor(Math.random() * values.length)];
      }

      if (patternName === "Pxrand") {
        // Exclusive random (no immediate repeats)
        let v;
        let safety = 0;
        do {
          v = values[Math.floor(Math.random() * values.length)];
          safety++;
        } while (v === last && values.length > 1 && safety < 100);
        last = v;
        return v;
      }

      if (patternName === "Pshuf") {
        // Shuffle, then play linearly
        const v = shuffled[index];
        index++;
        if (index >= shuffled.length) {
          index = 0;
          shuffled = shuffle(values); // Reshuffle next cycle
        }
        return v;
      }

      // Default: Pseq (sequential)
      const v = values[index];
      index++;
      if (index >= values.length) {
        index = 0;
      }
      return v;
    },

    reset() {
      index = 0;
      last = null;
      if (patternName === "Pshuf") {
        shuffled = shuffle(values);
      }
    }
  };
}

/**
 * Get or create a speed pattern generator for a cue. The generator is
 * keyed on (pattern, values) so it gets re-created when the user edits
 * pitchValues — without that check, a stale value from a previous edit
 * would survive across saves until a hard module reload.
 */
function getOrCreateSpeedPatternGenerator(uid, params) {
  const key = `${uid}-speed`;

  // DSL pattern object: speed:Pseq(...) / Prand(...) — supports nesting
  // and hrand() via the shared generator
  const dslPattern = params.speed ?? params.pitch;
  if (dslPattern && typeof dslPattern === "object" && dslPattern.type === "pattern") {
    const sig = "dsl|" + JSON.stringify(dslPattern);
    const cached = speedPatternGenerators.get(key);
    if (cached && cached._sig === sig) return cached;
    let inner = makePatternGenerator(dslPattern);
    const gen = {
      next: () => inner?.next() ?? null,
      reset: () => { inner = makePatternGenerator(dslPattern); },
      _sig: sig,
    };
    speedPatternGenerators.set(key, gen);
    return gen;
  }

  // Session-layer config (pitchPattern/pitchValues from the audio object editor)
  const pattern = params.pitchPattern ?? "Prand";
  const values = params.pitchValues ?? [1];
  const sig = `${pattern}|${values.join(",")}`;
  const cached = speedPatternGenerators.get(key);
  if (cached && cached._sig === sig) return cached;
  const gen = makeSpeedPatternGenerator(pattern, values);
  gen._sig = sig;
  speedPatternGenerators.set(key, gen);
  return gen;
}

/**
 * Reset a speed pattern generator (call when cue state resets).
 */
function resetSpeedPatternGenerator(uid) {
  const key = `${uid}-speed`;
  if (speedPatternGenerators.has(key)) {
    speedPatternGenerators.get(key).reset();
  }
}

// =============================================================
//  ensureAudioPool -- the folder's file list, cached
//
//  From a folder picked on this computer (mediaFolder.js) when
//  it has the path, else from the server. glob: and format:
//  narrow it (either way). Returns a pool object
//  { files, mode, cursor } that persists across triggers for
//  the same uid.  The cursor tracks position for sequential and
//  shuffle modes.
// =============================================================

// another folder picked: the lists start again
window.addEventListener("oscilla:media-folder", () => audioPools.clear());

export async function ensureAudioPool(uid, params) {
  if (audioPools.has(uid)) return audioPools.get(uid);

  const { path, project, glob, format } = params;

  let files = listDir(path, "audio");

  if (!files) {
    const projectName = project || window.currentProject || "default";
    try {
      const res = await fetch(`/api/audio-list/${projectName}/${path}`);
      const json = await res.json();
      files = Array.isArray(json.files) ? json.files : [];
    } catch (err) {
      // no server (Live on a static host): the list the build wrote beside
      // the sounds it carries (build-live.mjs: shared/audio/hits/)
      try {
        const res = await fetch(`${window.sharedDir || "shared/"}audio/${path}/index.json`);
        files = res.ok ? await res.json() : [];
      } catch {
        files = [];
      }
      if (!files.length) console.warn(`[audioPool] Failed to load audio list for ${path}${missNote(path, "audio")}`, err);
    }
  }
  files = filterPool(files, { glob, format });

  const pool = {
    files,
    mode: params.mode || "shuffle",
    cursor: 0
  };

  // Only cache non-empty pools -- empty results may resolve later
  // (e.g. after a folder is picked)
  if (files.length > 0) {
    audioPools.set(uid, pool);
  }
  return pool;
}

// =============================================================
//  handleAudioPoolCue -- single hit from randomised pool
//
//  Each trigger:
//    1. Selects a file from the cached pool (shuffle/rand/seq)
//    2. Evaluates randomisable params (amp, pan, speed)
//    3. Enforces poly limit (unlimited by default)
//    4. Delegates to handleAudioCue with a unique playUid
//       (always unique so it never hits the live-update path)
//    5. Adds a per-voice peak layer + cursor to the waveform
//       (both auto-removed when the voice ends)
// =============================================================

export async function handleAudioPoolCue(ast, el, opts = {}) {
  try {
    const params = ast.params || ast;

    const {
      path,
      glob,
      format = "wav",
      mode = "shuffle",
      uid = `${path || "pool"}-${glob || "all"}`,

      amp,
      fade,
      loop = 1,

      osc,
      oscaddr,
      waveform  // waveform target: "self" (default), "none", or element id
    } = params;

    if (!path) {
      console.warn("[audioPool] Missing path:", params);
      return { selectedFile: null };
    }

    // From the console: a box of its own, the pool's waveforms in it
    const box = (el || params.singleFile) ? null : await consoleBox(ast, uid, primePoolWaveform);
    el ??= box;

    // ---------------------------------------------------------
    //  Single file mode: path is the full file path, skip pool
    //  Used when audio type has poly params and routes here
    // ---------------------------------------------------------
    const singleFile = params.singleFile ?? false;
    let file, pool;

    if (singleFile) {
      // Single file mode: use path directly, no directory listing needed
      file = opts.syncedFile || path.split("/").pop();  // Just the filename
      pool = { files: [file], mode: "sequential", cursor: 0 };
    } else {
      // ---------------------------------------------------------
      //  Pool: fetch file list from server (cached after first call)
      // ---------------------------------------------------------
      pool = await ensureAudioPool(uid, { path, glob, format: params.format, mode });

      if (!pool?.files?.length) {
        console.warn(`[audioPool] no files in ${path}${glob ? ` matching ${glob}` : ""}${params.format ? ` of format ${params.format}` : ""}`);
        return { selectedFile: null };
      }

      // ---------------------------------------------------------
      //  File selection: use synced file if provided, else select from pool
      // ---------------------------------------------------------
      file = opts.syncedFile || selectFromPool(pool);
    }

    if (!file) {
      console.warn("[audioPool] Pool selection returned null:", params);
      return { selectedFile: null };
    }

    // ---------------------------------------------------------
    //  Evaluate randomisable params (may be rand/irand exprs)
    //  Use synced values if provided, otherwise evaluate locally
    // ---------------------------------------------------------
    let evaluatedAmp, panVal, speedVal;

    if (opts.syncedAmp !== undefined && opts.syncedPan !== undefined && opts.syncedSpeed !== undefined) {
      // Use synced values from remote client
      evaluatedAmp = opts.syncedAmp;
      panVal = opts.syncedPan;
      speedVal = opts.syncedSpeed;
    } else {
      // Evaluate locally: rand() rolled, a follow()/map() read off its
      // source -- every shot takes the signal as it is when it fires
      const baseAmp = valueNow(amp) ?? 1;
      const basePan = valueNow(params.pan) ?? 0;
      const baseSpeed = valueNow(params.speed ?? params.pitch) ?? 1;

      // Per-hit randomisation from panRandom params
      const panRandom = Number(params.panRandom ?? 0);
      const reverse = params.reverse ?? false;
      const bidirectional = params.bidirectional ?? false;

      const randAround = (base, range, min = -Infinity, max = Infinity) => {
        if (!range || range <= 0) return base;
        const delta = (Math.random() * 2 - 1) * range;
        return Math.max(min, Math.min(max, base + delta));
      };

      evaluatedAmp = Math.max(0, Math.min(1, baseAmp));
      panVal = randAround(basePan, panRandom, -1, 1);

      // Speed calculation based on pitch mode (none, range, pattern)
      const dslSpeedPattern = params.speed ?? params.pitch;
      const pitchMode = params.pitchMode
        ?? ((dslSpeedPattern && typeof dslSpeedPattern === "object" && dslSpeedPattern.type === "pattern") ? "pattern" : "none");
      const oldPitchRandom = Number(params.speedRandom ?? params.pitchRandom ?? 0);

      // Backward compatibility: convert old pitchRandom to range mode
      if (oldPitchRandom > 0 && pitchMode === "none") {
        if (bidirectional) {
          const magnitude = Math.max(0.01, baseSpeed + oldPitchRandom - 1);
          speedVal = (Math.random() * 2 - 1) * magnitude;
        } else if (reverse) {
          const lo = -(baseSpeed + oldPitchRandom);
          const hi = -Math.max(0.01, baseSpeed - oldPitchRandom);
          speedVal = lo + Math.random() * (hi - lo);
        } else {
          speedVal = randAround(baseSpeed, oldPitchRandom);
          speedVal = Math.max(0.01, speedVal);
        }
      } else if (pitchMode === "range") {
        // Range mode: pick random value between min and max
        const min = params.pitchMin ?? 0.8;
        const max = params.pitchMax ?? 1.2;
        if (bidirectional) {
          // 50/50 chance of positive or negative range
          const useNegative = Math.random() < 0.5;
          speedVal = min + Math.random() * (max - min);
          if (useNegative) speedVal = -speedVal;
        } else if (reverse) {
          speedVal = -(min + Math.random() * (max - min));
        } else {
          speedVal = min + Math.random() * (max - min);
        }
      } else if (pitchMode === "pattern") {
        // Pattern mode: use pattern generator to get next value
        const generator = getOrCreateSpeedPatternGenerator(uid, params);
        let val = generator.next();
        if (val === null) {
          // Pattern exhausted, reset and get next
          resetSpeedPatternGenerator(uid);
          val = getOrCreateSpeedPatternGenerator(uid, params).next() ?? 1;
        }
        // Pattern steps may be rand()/hrand() funcCalls — evaluate per hit
        const evaluated = evalMaybeRandom(val);
        if (typeof evaluated === "number" && Number.isFinite(evaluated)) val = evaluated;
        if (bidirectional) val = nextBidirectionalSpeed(uid, Math.abs(val));
        else if (reverse && val > 0) val = -Math.abs(val);
        speedVal = val;
      } else {
        // "none" mode - static speed; bidirectional alternates direction
        // per trigger (forward, reverse, forward, ...).
        if (bidirectional) {
          speedVal = nextBidirectionalSpeed(uid, baseSpeed);
        } else {
          speedVal = reverse ? -baseSpeed : baseSpeed;
        }
      }

      // Avoid zero zone for bidirectional
      if (bidirectional && Math.abs(speedVal) < 0.01) {
        speedVal = speedVal >= 0 ? 0.01 : -0.01;
      }
    }

    // fadeIn: / fadeOut: as the inspector writes them, fadein: / fadeout: as
    // the docs do; fade: is both
    const fadeInVal = params.fadeIn ?? params.fadein ?? fade ?? 0;
    const fadeOutVal = params.fadeOut ?? params.fadeout ?? fade ?? 0;

    // Only append format if file has no recognized audio extension
    const hasAudioExt = /\.(wav|mp3|ogg|webm|flac|aac|m4a|aiff?)$/i.test(file);
    let name = hasAudioExt ? file : `${file}.${format}`;
    // In single file mode, path is already the full file path
    const filename = singleFile ? path : (path ? `${path}/${name}` : name);

    // ---------------------------------------------------------
    //  Polyphony: unlimited by default, cap only if poly:N set.
    //  When at capacity, oldest voice is stopped (not skipped).
    // ---------------------------------------------------------
    const poly = Number(params.poly ?? (params.overlap ? 99 : 0));
    const polyLimit = poly > 0 ? poly : Infinity;

    // Always unique playUid -- audioPool plays a different file each
    // trigger, so it must never hit handleAudioCue's live-update
    // retrigger path (which is designed for audioFile drones).
    const playUid = `${uid}__${Date.now()}_${Math.floor(Math.random() * 9999)}`;

    // Enforce poly limit: stop oldest voice when at capacity
    const reg = window.activeAudioCues;
    if (reg && polyLimit < Infinity) {
      const activeKeys = [];
      for (const key of reg.keys()) {
        if (key === uid || key.startsWith(`${uid}__`)) {
          activeKeys.push(key);
        }
      }
      while (activeKeys.length >= polyLimit) {
        const oldest = activeKeys.shift();
        const voice = reg.get(oldest);
        try { voice?.stop?.(0.03); } catch (err) { console.warn("[audioPool] voice stop failed:", err); }
        reg.delete(oldest);
      }
    }

    // ---------------------------------------------------------
    //  Build cue object for handleAudioCue
    //  waveform: "none" prevents handleAudioCue from creating its own
    //  waveform -- audioPool handles waveforms via peak layers above.
    // ---------------------------------------------------------
    const cue = {
      type: "cueAudio",
      src: filename,
      uid: playUid,
      amp: Math.max(0, Math.min(1, Number(evaluatedAmp))),
      fadeIn: fadeInVal,
      fadeOut: fadeOutVal,
      loop: loop === 0 ? 0 : (Number(loop) || 1),  // Preserve 0 for infinite loop
      toggle: false,
      pan: panVal,
      speed: speedVal,
      waveform: "none",
      _box: box,
      // Effects pass-through
      filter: params.filter ?? null,
      delay: params.delay ?? null,
      reverb: params.reverb ?? null,
      outputBus: params.outputBus ?? null
    };

    // ---------------------------------------------------------
    //  OVERLAY -- show filename + evaluated params
    //  Suppressed when waveform is active (info shown as SVG text)
    // ---------------------------------------------------------
    const wfParam = waveform ?? "self";
    const wfActive = wfParam !== "none" && wfParam !== "0" && el;

    if (el && !wfActive) {
      const overlay = createAudioOverlay({
        anchorEl: el,
        label: `${path}`
      });

      const details = [
        `amp:${fmtNum(evaluatedAmp)}`,
        `pan:${fmtNum(panVal)}`,
        `speed:${fmtNum(speedVal)}`
      ].join(" ");

      overlay.update(`${file} | ${details}`);
      overlay.position();

      setTimeout(() => {
        overlay.destroy();
      }, 1500);
    }

    // ---------------------------------------------------------
    //  OSC -- optional network announcement
    // ---------------------------------------------------------
    if (osc || oscaddr) {
      try {
        sendOSC({
          type: "osc_audio_pool",
          uid,
          filename,
          amp: cue.amp,
          fadeIn: cue.fadeIn,
          fadeOut: cue.fadeOut,
          pan: cue.pan,
          speed: cue.speed,
          addr: oscaddr || null   // server default: /oscilla/audio/pool
        });
      } catch (err) {
        console.warn("[audioPool OSC] failed:", err);
      }
    }

    // ---------------------------------------------------------
    //  Fire audio playback via the shared audio engine
    // ---------------------------------------------------------
    await handleAudioCue(cue);

    // ---------------------------------------------------------
    //  WAVEFORM -- per-voice peak layer + cursor
    //
    //  Lifecycle:
    //    prime  -> preview layers (up to 5 files, 0.25 opacity)
    //    first trigger -> previews cleared, per-voice layer + cursor
    //    voice end -> layer + cursor removed
    //    last voice end -> previews restored, info text hidden
    //    next trigger -> previews cleared again, cycle repeats
    // ---------------------------------------------------------
    if (wfActive) {
      // For singleFile mode (audio with poly), waveform is registered without -pool- prefix
      const wfUid = singleFile ? uid : `wf-pool-${uid}`;
      const wfHandle = getWaveform(wfUid);
      if (wfHandle) {
        // Update header with current filename
        if (wfHandle._headerDiv) {
          const headerStr = buildAudioInfoHeader({
            audioObjectType: params.audioObjectType || "audioPool",
            audioObjectLabel: params.audioObjectLabel,
            filename: file,
            playbackClients: getPlaybackClientsFromEl(el)
          });
          wfHandle._headerDiv.textContent = headerStr;
        }

        // Update details text with resolved per-hit params
        // Respect visibility state - don't show details if toggle is off
        if (wfHandle._detailsDiv || wfHandle._infoText) {
          const parts = [];
          parts.push(`amp:${fmtNum(evaluatedAmp)}`);
          parts.push(`speed:${fmtNum(speedVal)}`);
          parts.push(`pan:${fmtNum(panVal)}`);
          const detailsStr = parts.join(" | ");

          if (wfHandle._detailsDiv) {
            wfHandle._detailsDiv.textContent = detailsStr;
            // Ensure visibility respects the handle's state
            wfHandle._detailsDiv.style.display = wfHandle._labelVisible ? "" : "none";
          } else if (wfHandle._infoText) {
            wfHandle._infoText.textContent = detailsStr;
          }
          // Only update layout if details are visible
          if (wfHandle._labelVisible) {
            updateWaveformLayout(wfHandle);
          }
        }

        const buf = audioBufferCache.get(filename);
        if (buf) {
          // On first live trigger, clear the primed preview layers
          // so only actual playing voices are shown
          if (!wfHandle._previewCleared) {
            wfHandle._previewCleared = true;
            removeAllPeakLayers(wfHandle);
          }

          // Details visibility is controlled by the global toggle, don't override here

          // Add coloured peak layer for this voice
          addPeakLayer(wfHandle, buf, filename, { id: playUid });
          const layerColor = wfHandle._peakLayers?.get(playUid)?.color || "#c00";

          if (sharedAudioCtx) {
            const ctx = sharedAudioCtx;

            // Per-voice sub-cursor, colour-matched to its peak layer
            const subCursor = addCursor(wfHandle, playUid, {
              color: layerColor, width: "1.6", opacity: "0.55"
            });

            if (subCursor) {
              // Pass loop parameter so cursor wraps for looping audio
              const loopCount = loop === 0 ? 0 : (Number(loop) || 1);
              startCursor(subCursor, ctx, ctx.currentTime, buf.duration, speedVal, loopCount);

              // Auto-remove cursor and peak layer when this voice ends
              const onStop = (e) => {
                if (e.detail?.uid === playUid && e.detail?.state === "stop") {
                  removeCursor(wfHandle, playUid);
                  removePeakLayer(wfHandle, playUid);
                  window.removeEventListener("oscilla:audio", onStop);

                  // When all voices are done, restore primed preview state
                  const hasLayers = wfHandle._peakLayers && wfHandle._peakLayers.size > 0;
                  const hasCursors = wfHandle._cursors && wfHandle._cursors.size > 0;
                  if (!hasLayers && !hasCursors) {
                    // Hide per-trigger details (header stays visible)
                    if (wfHandle._detailsDiv && !window.waveformLabelsVisible) {
                      wfHandle._detailsDiv.style.display = "none";
                    } else if (wfHandle._infoText) {
                      wfHandle._infoText.setAttribute("opacity", "0");
                    }

                    // Re-add preview layers from stored data
                    if (wfHandle._previewData?.length) {
                      for (const { filename: pf, buf: pb } of wfHandle._previewData) {
                        addPeakLayer(wfHandle, pb, pf, {
                          id: `preview-${pf}`,
                          opacity: "0.75"
                        });
                      }
                    }

                    // Allow next trigger to clear previews again
                    wfHandle._previewCleared = false;
                  }
                }
              };
              window.addEventListener("oscilla:audio", onStop);
            }
          }
        }
      }
    }

    // Return evaluated values so callers can sync them to other clients
    return { selectedFile: file, amp: evaluatedAmp, pan: panVal, speed: speedVal };

  } catch (err) {
    console.error("[audioPool] error:", err);
    return { selectedFile: null };
  }
}

// =============================================================
//  Prime audioPool overlay (called during assignCues)
//
//  Creates an HTML overlay showing pool params on the score.
//  Skipped when waveform display is active (the waveform's
//  own info text serves the same purpose).
// =============================================================

export function primeAudioPoolOverlay(ast, cueElement) {
  if (!ast || !cueElement) return;

  const params = ast.params || ast;
  const overlayLevel = parseOverlayLevel(params.overlay ?? ast.overlay);
  if (overlayLevel <= 0) return;

  // Skip HTML overlay if waveform display is active (it has its own info text)
  const waveformParam = params.waveform ?? "self";
  if (waveformParam !== "none" && waveformParam !== "0") return;

  if (cueElement._audioPoolOverlayPrimed) return;
  cueElement._audioPoolOverlayPrimed = true;

  const overlay = createAudioOverlay({
    anchorEl: cueElement,
    label: "audioPool",
    track: true
  });

  if (overlay?.el) {
    const text = formatAudioPoolOverlay(params, overlayLevel);
    overlay.update(text);
    overlay.position();
    cueElement._audioPoolOverlay = overlay;
    cueElement._audioPoolOverlayLevel = overlayLevel;
  }
}

// =============================================================
//  Prime pool waveform (called during assignCues)
//
//  Creates the waveform handle and renders preview layers showing
//  up to 5 files from the pool at reduced opacity.  This gives
//  a visual preview of the pool's sonic content before any
//  trigger fires.
//
//  The base contour (from renderWaveform) is hidden immediately --
//  all visible contours come from peak layers, either:
//    - preview layers (shown at prime, cleared on first trigger)
//    - per-voice layers (shown during playback, removed on voice end)
// =============================================================

const MAX_PREVIEW_LAYERS = 5;

export async function primePoolWaveform(ast, cueElement) {
  if (!ast || !cueElement) return;

  const params = ast.params || ast;
  const waveformParam = params.waveform ?? "self";

  // Skip if waveform display is disabled
  if (waveformParam === "none" || waveformParam === "0") return;

  // Prevent double-priming on the same element
  if (cueElement._poolWaveformPrimed) return;
  cueElement._poolWaveformPrimed = true;

  const svg = cueElement.ownerSVGElement;
  if (!svg) return;

  const {
    path,
    glob,
    format = "wav",
    mode = "shuffle",
    uid = `${path || "pool"}-${glob || "all"}`
  } = params;

  if (!path) return;

  // Register task SYNCHRONOUSLY before any async work
  // This ensures the progress indicator waits for pool waveform loading
  const taskId = `pool-waveform:${uid}`;
  if (!window._audioTaskRegistered) window._audioTaskRegistered = new Set();
  if (!window._audioTaskRegistered.has(taskId)) {
    window._audioTaskRegistered.add(taskId);
    registerTask(taskId, `Pool: ${path}`);
  }

  try {
    const pool = await ensureAudioPool(uid, { path, glob, format: params.format, mode });
    if (!pool?.files?.length) return;

    const ctx = sharedAudioCtx;

    // ---------------------------------------------------------
    //  Helper: fetch, decode, and cache a single pool file
    // ---------------------------------------------------------
    const loadFile = async (file) => {
      let filename = path ? `${path}/${file}` : file;
      // Only append format if file has no recognized audio extension
      const hasAudioExt = /\.(wav|mp3|ogg|webm|flac|aac|m4a|aiff?)$/i.test(filename);
      if (!hasAudioExt) filename = `${filename}.${format}`;

      const buf = await fetchAudioBuffer(ctx, filename);
      return { filename, buf };
    };

    // ---------------------------------------------------------
    //  Render base waveform group using the first file.
    //  This creates the SVG group, bbox, handle, and info text.
    //  The base contour itself is hidden -- only peak layers
    //  are visible (previews now, per-voice layers at runtime).
    // ---------------------------------------------------------
    const first = await loadFile(pool.files[0]);

    // Build header (always visible) and details (toggleable) separately
    const headerStr = buildAudioInfoHeader({
      audioObjectType: params.audioObjectType || "audioPool",
      audioObjectLabel: params.audioObjectLabel,
      filename: first.filename,
      playbackClients: getPlaybackClientsFromEl(cueElement)
    });
    const detailsParts = [`path:${path}`];
    if (params.mode && params.mode !== "shuffle") detailsParts.push(`mode:${params.mode}`);
    if (params.poly && params.poly !== 1) detailsParts.push(`poly:${params.poly}`);
    const detailsStr = detailsParts.join(" | ");

    const wfHandle = renderWaveform(svg, waveformParam, first.buf, `wf-pool-${uid}`, first.filename, {
      element: cueElement,
      header: headerStr,
      info: detailsStr
    });

    if (!wfHandle) return;

    // Hide base contour -- audioPool uses peak layers exclusively
    if (wfHandle.upperLine) wfHandle.upperLine.setAttribute("opacity", "0");
    if (wfHandle.lowerLine) wfHandle.lowerLine.setAttribute("opacity", "0");

    // ---------------------------------------------------------
    //  Add preview layers for up to MAX_PREVIEW_LAYERS files.
    //  These are rendered at reduced opacity to give a visual
    //  preview of the pool contents.  Cleared on first trigger,
    //  restored when all voices complete.
    //
    //  Preview data is stored on the handle so the trigger
    //  path can restore previews without re-fetching.
    // ---------------------------------------------------------
    const previewCount = Math.min(MAX_PREVIEW_LAYERS, pool.files.length);
    const previewFiles = pool.files.slice(0, previewCount);

    // Store preview specs for restoration after all voices complete
    wfHandle._previewData = [];

    for (const file of previewFiles) {
      try {
        const { filename, buf } = await loadFile(file);
        addPeakLayer(wfHandle, buf, filename, {
          id: `preview-${filename}`,
          opacity: "0.75"
        });
        wfHandle._previewData.push({ filename, buf });
      } catch (err) {
        console.warn(`[audioPool] Preview layer failed for ${file}:`, err);
      }
    }

    // Preload remaining pool files in background (non-blocking)
    // so they're cached before first trigger
    if (pool.files.length > previewCount) {
      const remaining = pool.files.slice(previewCount);
      const preloadBatch = async () => {
        for (const file of remaining) {
          try { await loadFile(file); } catch (err) { console.warn(`[audioPool] Preload failed for ${file}:`, err); }
        }
      };
      preloadBatch(); // fire-and-forget
    }
  } catch (err) {
    console.warn(`[audioPool] Waveform prime failed:`, err);
  } finally {
    // Complete the task to update progress indicator
    completeTask(taskId);
    window._audioTaskRegistered?.delete(taskId);
  }
}
